/**
 * Subscription Checkout Modal Component - Web Version
 * Reuses the existing and proven StripePaymentWeb component for payment processing
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ViewStyle,
  TextStyle,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useTheme } from '~contexts/ThemeContext';
import { spacing, borderRadius } from '~constants/design';
import {
  SubscriptionPlanDetails,
  SubscriptionType,
} from '~types/subscription/subscription.types';
import { useCreateSubscription } from '~hooks/subscription/useCreateSubscription';
import StripePaymentWeb from '~components/StripePaymentWeb/StripePaymentWeb';

// Conditional Notifier import
let Notifier: any = null;
let NotifierComponents: any = null;

try {
  const notifierModule = require('react-native-notifier');
  Notifier = notifierModule.Notifier;
  NotifierComponents = notifierModule.NotifierComponents;
} catch (e) {
  // Notifier not available
}

interface SubscriptionCheckoutModalWebProps {
  isVisible: boolean;
  plan: SubscriptionPlanDetails;
  subscriptionType: SubscriptionType;
  onClose: () => void;
  onSuccess: (planId: string, type: SubscriptionType) => void;
}

const SubscriptionCheckoutModalWeb: React.FC<SubscriptionCheckoutModalWebProps> = ({
  isVisible,
  plan,
  subscriptionType,
  onClose,
  onSuccess,
}) => {
  const { colors } = useTheme();
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutateAsync: createSubscription } = useCreateSubscription();

  const amount =
    subscriptionType === SubscriptionType.MONTHLY
      ? plan.pricing.monthly
      : plan.pricing.oneTimePass;

  const styles = useMemo(
    () => ({
      modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' } as ViewStyle,
      modalContainer: {
        backgroundColor: colors.background,
        borderTopLeftRadius: borderRadius['3xl'],
        borderTopRightRadius: borderRadius['3xl'],
        paddingTop: spacing.lg,
        maxHeight: '90%',
      } as ViewStyle,
      handle: {
        width: 40,
        height: 4,
        backgroundColor: colors.border,
        borderRadius: borderRadius.full,
        alignSelf: 'center',
        marginBottom: spacing.lg,
      } as ViewStyle,
      scrollContent: { padding: spacing.lg } as ViewStyle,
      header: { marginBottom: spacing.lg } as ViewStyle,
      planName: {
        fontSize: 22,
        fontWeight: '800',
        color: colors.text,
        marginBottom: spacing.xs,
      } as TextStyle,
      planType: { fontSize: 14, color: colors.textSecondary, marginBottom: spacing.lg } as TextStyle,
      summaryBox: {
        backgroundColor: colors.primary + '15',
        borderRadius: borderRadius.lg,
        padding: spacing.lg,
        marginBottom: spacing.lg,
        borderLeftWidth: 4,
        borderLeftColor: colors.primary,
      } as ViewStyle,
      summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: spacing.sm,
      } as ViewStyle,
      summaryLabel: { fontSize: 14, color: colors.textSecondary, fontWeight: '500' } as TextStyle,
      summaryValue: { fontSize: 16, fontWeight: '700', color: colors.text } as TextStyle,
      totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTopWidth: 1,
        borderTopColor: colors.primary + '30',
        marginTop: spacing.sm,
        paddingTop: spacing.sm,
      } as ViewStyle,
      totalValue: { fontSize: 18, fontWeight: '800', color: colors.primary } as TextStyle,
      termsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: spacing.lg,
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.md,
        backgroundColor: colors.gray50,
        borderRadius: borderRadius.lg,
      } as ViewStyle,
      termsCheckbox: {
        width: 24,
        height: 24,
        borderRadius: 6,
        borderWidth: 2,
        borderColor: agreedToTerms ? colors.primary : colors.border,
        backgroundColor: agreedToTerms ? colors.primary : 'transparent',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: spacing.sm,
        marginTop: 2,
      } as ViewStyle,
      termsText: { fontSize: 12, color: colors.textSecondary, flex: 1, lineHeight: 18 } as TextStyle,
      buttonContainer: {
        flexDirection: 'row',
        gap: spacing.md,
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.lg,
      } as ViewStyle,
      primaryButton: {
        flex: 1,
        backgroundColor: colors.primary,
        paddingVertical: spacing.lg,
        borderRadius: borderRadius.lg,
        alignItems: 'center',
        justifyContent: 'center',
      } as ViewStyle,
      secondaryButton: {
        flex: 1,
        backgroundColor: colors.gray50,
        paddingVertical: spacing.lg,
        borderRadius: borderRadius.lg,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: colors.border,
      } as ViewStyle,
      buttonText: { fontSize: 15, fontWeight: '700', letterSpacing: 0.5 } as TextStyle,
      infoBanner: {
        backgroundColor: colors.background,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.lg,
        borderRadius: borderRadius.lg,
        marginBottom: spacing.lg,
        borderWidth: 1,
        borderColor: colors.border + '30',
      } as ViewStyle,
      infoBannerText: { fontSize: 12, color: colors.textSecondary, lineHeight: 16, textAlign: 'center' as any } as TextStyle,
      errorBanner: {
        backgroundColor: '#fee2e2',
        borderRadius: borderRadius.lg,
        padding: spacing.md,
        marginBottom: spacing.lg,
        borderLeftWidth: 4,
        borderLeftColor: '#dc2626',
      } as ViewStyle,
      errorText: { fontSize: 12, color: '#991b1b', lineHeight: 16 } as TextStyle,
    }),
    [colors, agreedToTerms]
  );

  const handleInitiatePayment = useCallback(async () => {
    if (!agreedToTerms) {
      Alert.alert('Agreement Required', 'Please agree to the terms and conditions to proceed.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      console.log('🔍 Initiating subscription for plan:', plan.id);
      const subscriptionData = await createSubscription({
        plan_id: plan.id,
      });

      console.log('✅ Subscription created, showing payment form');
      setClientSecret(subscriptionData.client_secret);
      setIsProcessing(false);
    } catch (error: any) {
      console.error('❌ Failed to create subscription:', error);
      const errorMsg = error.message || 'Failed to initiate subscription. Please try again.';
      setErrorMessage(errorMsg);
      setIsProcessing(false);

      Alert.alert('Error', errorMsg);
    }
  }, [agreedToTerms, plan.id, createSubscription]);

  const handlePaymentSuccess = useCallback(
    (paymentIntentId: string) => {
      console.log('✅ Payment successful:', paymentIntentId);
      onSuccess(plan.id, subscriptionType);

      // Show success notification if available
      if (Notifier?.showNotification && NotifierComponents) {
        try {
          Notifier.showNotification({
            title: 'Subscription Activated!',
            description: `You've successfully subscribed to ${plan.name}.`,
            Component: NotifierComponents.Alert,
            componentProps: {
              alertType: 'success',
            },
          });
        } catch (e) {
          console.warn('Could not show notification:', e);
        }
      }

      // Close modal after short delay
      setTimeout(() => {
        setClientSecret(null);
        setAgreedToTerms(false);
        onClose();
      }, 500);
    },
    [plan.id, plan.name, subscriptionType, onSuccess, onClose]
  );

  const handlePaymentError = useCallback((error: string) => {
    console.error('❌ Payment error:', error);
    setErrorMessage(error);
  }, []);

  const handleClosePaymentForm = useCallback(() => {
    setClientSecret(null);
    setErrorMessage(null);
  }, []);

  // If payment form is open, show only the payment form
  if (clientSecret) {
    return (
      <Modal
        visible={isVisible && !!clientSecret}
        transparent
        animationType="slide"
        onRequestClose={handleClosePaymentForm}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.handle} />

            <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
              <Text style={styles.planName}>{plan.name}</Text>
              <Text style={styles.planType}>Complete Payment</Text>

              {/* Error Message */}
              {errorMessage && (
                <View style={styles.errorBanner}>
                  <Text style={styles.errorText}>{errorMessage}</Text>
                </View>
              )}

              {/* Stripe Payment Form */}
              <StripePaymentWeb
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                merchantDisplayName="Pyxi"
              />
            </ScrollView>

            {/* Close Button */}
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.secondaryButton}
                onPress={handleClosePaymentForm}
                disabled={false}>
                <Text style={{ ...styles.buttonText, color: colors.text }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  // Initial modal - show plan details and terms
  return (
    <Modal
      visible={isVisible && !clientSecret}
      transparent
      animationType="slide"
      onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.handle} />

          <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.planName}>{plan.name}</Text>
              <Text style={styles.planType}>
                {subscriptionType === SubscriptionType.MONTHLY ? 'Monthly Subscription' : 'One-Time Pass'}
              </Text>
            </View>

            {/* Order Summary */}
            <View style={styles.summaryBox}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Plan Name</Text>
                <Text style={styles.summaryValue}>{plan.shortName || plan.name}</Text>
              </View>

              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Billing Cycle</Text>
                <Text style={styles.summaryValue}>
                  {subscriptionType === SubscriptionType.MONTHLY ? 'per month' : 'one-time'}
                </Text>
              </View>

              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Subtotal</Text>
                <Text style={styles.summaryValue}>£{amount?.toFixed(2)}</Text>
              </View>

              <View style={styles.totalRow}>
                <Text style={{ ...styles.summaryLabel, fontSize: 16, fontWeight: '700' }}>Total Amount</Text>
                <Text style={styles.totalValue}>£{amount?.toFixed(2)}</Text>
              </View>
            </View>

            {/* What's Included */}
            <View style={styles.infoBanner}>
              <Text
                style={{
                  ...styles.infoBannerText,
                  marginBottom: spacing.sm,
                  fontWeight: '600',
                  color: colors.text,
                  textAlign: 'left',
                }}>
                What's Included:
              </Text>
              {plan.features
                .filter((f) => f.included)
                .map((feature, idx) => (
                  <Text
                    key={feature.id}
                    style={{
                      ...styles.infoBannerText,
                      color: colors.text,
                      textAlign: 'left',
                      marginBottom:
                        idx < plan.features.filter((f) => f.included).length - 1 ? spacing.xs : 0,
                    }}>
                    • {feature.name}
                  </Text>
                ))}
            </View>

            {/* Error Message */}
            {errorMessage && (
              <View style={styles.errorBanner}>
                <Text style={styles.errorText}>{errorMessage}</Text>
              </View>
            )}

            {/* Terms & Conditions */}
            <TouchableOpacity
              style={styles.termsContainer}
              onPress={() => setAgreedToTerms(!agreedToTerms)}
              activeOpacity={0.7}>
              <View style={styles.termsCheckbox}>
                {agreedToTerms && (
                  <Text style={{ fontSize: 14, fontWeight: '700', color: '#FFFFFF' }}>✓</Text>
                )}
              </View>
              <Text style={styles.termsText}>
                I agree to the Subscription Terms & Conditions and Privacy Policy. My subscription will
                auto-renew on the same date each{' '}
                {subscriptionType === SubscriptionType.MONTHLY ? 'month' : 'year'}.
              </Text>
            </TouchableOpacity>

            {/* Important Info */}
            <View
              style={{
                backgroundColor: colors.gray50,
                padding: spacing.md,
                borderRadius: borderRadius.lg,
                marginBottom: spacing.lg,
              }}>
              <Text style={{ fontSize: 12, color: colors.black, lineHeight: 16 }}>
                💳 Your payment is secure and encrypted.{'\n'}You can cancel anytime from your account
                settings.
              </Text>
            </View>
          </ScrollView>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={onClose}
              disabled={isProcessing}
              activeOpacity={0.8}>
              <Text style={{ ...styles.buttonText, color: colors.text }}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.primaryButton, !agreedToTerms && { opacity: 0.6 }]}
              onPress={handleInitiatePayment}
              disabled={!agreedToTerms || isProcessing}
              activeOpacity={0.85}>
              {isProcessing ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={{ ...styles.buttonText, color: '#FFFFFF' }}>Pay £{amount?.toFixed(2)}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default SubscriptionCheckoutModalWeb;
